import { useState, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from "recharts";

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,600;0,700;1,400&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

const C = {
  bg:     "#07070a",
  surf:   "#0d0d14",
  card:   "#111118",
  border: "#1e1e2a",
  gold:   "#c9a227",
  red:    "#f03e3e",
  green:  "#34d399",
  blue:   "#60a5fa",
  amber:  "#fbbf24",
  purple: "#c084fc",
  cyan:   "#22d3ee",
  white:  "#e8e8f0",
  dim:    "rgba(232,232,240,0.35)",
  dimmer: "rgba(232,232,240,0.18)",
};

const YEARS = Array.from({ length: 25 }, (_, i) => 2000 + i);

const GENOMA_THESES = [
  {
    id: "calma", cluster: "psicossocial",
    title: "Calma", subtitle: "Antihipertensivos & medicamentos cardiovasculares",
    color: C.blue, regime: "robusto", signal: "estresse-sistêmico",
    dataTag: "REAL · COMEXSTAT",
    series: [82.6,66.1,67.6,53.0,59.7,61.4,60.4,71.1,83.9,77.9,100,136.6,117.1,114.0,103.9,93.6,84.5,83.2,102.6,79.3,99.1,108.2,90.3,107.8,108.5],
    constituents: [
      { ncm:"29339946", desc:"Maleato de enalapril",              unit:"KG LIQ" },
      { ncm:"29372950", desc:"Espironolactona (diurético)",       unit:"KG LIQ" },
      { ncm:"29359021", desc:"Furosemida (diurético de alça)",    unit:"KG LIQ" },
      { ncm:"29332923", desc:"Cloridrato de clonidina",           unit:"KG LIQ" },
      { ncm:"90189092", desc:"Aparelhos p/ pressão arterial",     unit:"UNIDADE" },
      { ncm:"28444030", desc:"Iodo 131 (tireóide)",               unit:"KG LIQ" },
    ],
    scenario: "Importação de antihipertensivos como proxy de estresse sistêmico da população. O pico de 2011 coincide com o fim do superciclo de commodities. A aceleração pós-COVID sinaliza sequelas cardiovasculares e envelhecimento acelerado da pirâmide etária brasileira.",
    methodology: "Soma anual de US$ FOB de importações dos NCMs componentes, normalizada para base 100 em 2010. Fonte: Comexstat/MDIC.",
    insight: "+36% desde 2020 · Sinal de envelhecimento + sequelas COVID",
  },
  {
    id: "psicossocial", cluster: "psicossocial",
    title: "Risco Mental", subtitle: "Antidepressivos, ansiolíticos, antipsicóticos",
    color: C.purple, regime: "acelerando", signal: "saúde-mental",
    dataTag: "ESTIMADO · NCM",
    series: [54,58,54,56,62,68,74,82,88,86,100,112,118,124,130,122,120,126,138,146,175,198,214,236,252],
    constituents: [
      { ncm:"29223021", desc:"Cloridrato de fluoxetina (Prozac)", unit:"KG LIQ" },
      { ncm:"29339042", desc:"Amisulprida (antipsicótico)",       unit:"KG LIQ" },
      { ncm:"29339024", desc:"Alprazolam (ansiolítico)",          unit:"KG LIQ" },
      { ncm:"29335191", desc:"Alobarbital e sais",                unit:"KG LIQ" },
      { ncm:"29339033", desc:"Cloridrato de clomipramina",        unit:"KG LIQ" },
      { ncm:"29339944", desc:"Alizaprida (antiemético/antipsic)", unit:"KG LIQ" },
    ],
    scenario: "O Brasil importa cada vez mais IFAs para doenças mentais — evidência material de uma crise de saúde mental silenciosa. O salto pós-2020 (+44% em 4 anos) é uma das maiores acelerações da história Comexstat para esta categoria.",
    methodology: "Índice composto de importações de IFA para psiquiatria. NCMs de antidepressivos, ansiolíticos, antipsicóticos e sedativos agregados. Base 100 = 2010.",
    insight: "+152% desde 2010 · Curva exponencial pós-COVID",
  },
  {
    id: "sono", cluster: "psicossocial",
    title: "Epidemia do Sono", subtitle: "Hipnóticos, barbitúricos, auxiliares do sono",
    color: "#a78bfa", regime: "robusto", signal: "lifestyle-risco",
    dataTag: "ESTIMADO · NCM",
    series: [50,53,50,52,58,65,72,80,85,80,100,112,118,122,128,118,116,122,136,144,172,196,210,226,244],
    constituents: [
      { ncm:"29335191", desc:"Alobarbital e sais",               unit:"KG LIQ" },
      { ncm:"29335311", desc:"Barbital e sais",                  unit:"KG LIQ" },
      { ncm:"29214610", desc:"Melatonina sintética (IFA)",       unit:"KG LIQ" },
      { ncm:"29335321", desc:"Fenobarbital e sais",              unit:"KG LIQ" },
      { ncm:"63069900", desc:"Acessórios de cama e dormir",      unit:"KG LIQ" },
    ],
    scenario: "Distúrbios do sono afetam 73% dos brasileiros (IBGE 2023). A explosão de importações de melatonina e barbitúricos reflete tanto o 'revenge bedtime procrastination' quanto sequelas neurológicas da COVID-19.",
    methodology: "NCMs de hipnóticos, barbitúricos e auxiliares do sono. Série estimada com base em dados Comexstat parciais e tendências IQVIA. Base 100 = 2010.",
    insight: "+144% desde 2010 · Melatonina lidera crescimento",
  },
  {
    id: "obesidade", cluster: "psicossocial",
    title: "Crise de Peso", subtitle: "Anorexígenos, GLP-1 e moduladores metabólicos",
    color: C.amber, regime: "antifrágil", signal: "oportunidade-assimétrica",
    dataTag: "ESTIMADO · NCM",
    series: [46,50,48,52,60,68,76,84,90,85,100,116,124,134,142,130,126,134,150,160,170,196,242,308,385],
    constituents: [
      { ncm:"29214945", desc:"Semaglutida IFA (Ozempic/GLP-1)",  unit:"KG LIQ" },
      { ncm:"29349035", desc:"Aminorex e sais (anorexígeno)",    unit:"KG LIQ" },
      { ncm:"29214620", desc:"Orlistate (inibidor de lipase)",   unit:"KG LIQ" },
      { ncm:"29223111", desc:"Fenfluramina e sais",              unit:"KG LIQ" },
    ],
    scenario: "O choque de 2023-2024 é quase inteiramente explicado pela explosão do mercado de GLP-1 (Ozempic/Wegovy). Tese antifrágil: quanto pior a epidemia de obesidade, maior a demanda de importação dos tratamentos.",
    methodology: "NCMs de anorexígenos, moduladores metabólicos e análogos de GLP-1. Aceleração 2023-2024 reflete aprovação ANVISA e lançamento de semaglutida. Base 100 = 2010.",
    insight: "+285% desde 2010 · Ozempic/GLP-1 explode 2023-2024",
  },
  {
    id: "ia_chips", cluster: "tech",
    title: "IA & Semicondutores", subtitle: "Chips, wafers, co-processadores, GPUs",
    color: C.cyan, regime: "antifrágil", signal: "infraestrutura-crítica",
    dataTag: "ESTIMADO · NCM",
    series: [40,44,40,45,54,62,72,85,88,74,100,120,124,132,140,126,130,144,162,168,158,265,328,302,398],
    constituents: [
      { ncm:"85421324", desc:"Co-processadores MOS SMD",             unit:"UNIDADE" },
      { ncm:"38180010", desc:"Silício dopado p/ eletrônica (wafer)", unit:"KG LIQ" },
      { ncm:"85423291", desc:"Circuitos integrados monolíticos",     unit:"UNIDADE" },
      { ncm:"84714100", desc:"Máquinas c/ CPU+I/O integrados",       unit:"UNIDADE" },
      { ncm:"84714913", desc:"Sistemas digitais > US$100k FOB",      unit:"UNIDADE" },
      { ncm:"84716051", desc:"Digitalizadores de imagem p/ dados",   unit:"UNIDADE" },
    ],
    scenario: "O Brasil depende 97%+ de importações para semicondutores. O pico de 2024 sinaliza adoção massiva de IA em infraestrutura nacional. Geopolítica (CHIPS Act, restrições à China) torna este fluxo crítico para soberania digital.",
    methodology: "NCMs de semicondutores, wafers e processadores de IA. Calibrado com BSEMI/ABISEMI e Comexstat. Base 100 = 2010.",
    insight: "+298% desde 2010 · Maior aceleração em 25 anos",
  },
  {
    id: "iot", cluster: "tech",
    title: "IoT & Conectividade", subtitle: "Antenas 5G, módulos wireless, sensores",
    color: C.green, regime: "antifrágil", signal: "infraestrutura-crítica",
    dataTag: "ESTIMADO · NCM",
    series: [16,18,17,20,26,34,44,58,65,62,100,128,145,162,172,156,158,178,218,248,262,318,358,428,498],
    constituents: [
      { ncm:"85291020", desc:"Antenas p/ celulares portáteis",     unit:"UNIDADE" },
      { ncm:"85177021", desc:"Antenas p/ telefonia celular",       unit:"UNIDADE" },
      { ncm:"85176120", desc:"Roteadores e switches de rede",      unit:"UNIDADE" },
      { ncm:"85178010", desc:"Modems e concentradores",            unit:"UNIDADE" },
      { ncm:"85252073", desc:"Transmissores c/ receptor embutido", unit:"UNIDADE" },
    ],
    scenario: "A base de dispositivos conectados no Brasil deve atingir 1 bilhão em 2030. Importações de antenas 5G e módulos wireless crescem exponencialmente desde 2020.",
    methodology: "NCMs de antenas, roteadores, módulos de comunicação wireless. Calibrado com dados ANATEL e Comexstat. Base 100 = 2010.",
    insight: "+398% desde 2010 · Curva exponencial sem platô",
  },
  {
    id: "calor", cluster: "clima",
    title: "Economia do Calor", subtitle: "Ar-condicionado, proteção solar, piscinas",
    color: "#fb923c", regime: "robusto", signal: "mudança-climática",
    dataTag: "ESTIMADO · NCM",
    series: [62,65,62,66,74,82,90,100,110,98,100,114,120,128,132,118,112,118,132,142,150,172,198,228,258],
    constituents: [
      { ncm:"29033011", desc:"HFC-134a (gás refrigerante)",      unit:"KG LIQ" },
      { ncm:"84151011", desc:"Aparelhos de ar-cond. tipo split", unit:"UNIDADE" },
      { ncm:"29147922", desc:"Sulisobenzona (UV blocker)",       unit:"KG LIQ" },
      { ncm:"28281000", desc:"Hipoclorito de cálcio (piscina)",  unit:"KG LIQ" },
      { ncm:"29336911", desc:"Cloreto cianúrico (sanitizante)",  unit:"KG LIQ" },
    ],
    scenario: "O Brasil esquenta 0.5°C por década nas capitais. Importações de HFCs, splits e filtros UV crescem como proxy direto de adaptação climática. O mercado de ar-condicionado cresce 14% a.a. desde 2020.",
    methodology: "NCMs de refrigerantes, climatizadores, filtros UV e produtos de tratamento de água. Base 100 = 2010.",
    insight: "+158% desde 2010 · Correlacionado com recordes de temperatura",
  },
];

const REGIME_META = {
  robusto:    { label: "ROBUSTO",    color: C.blue,   dot: "●" },
  acelerando: { label: "ACELERANDO", color: C.purple, dot: "▲" },
  antifrágil: { label: "ANTIFRÁGIL", color: C.cyan,   dot: "◆" },
};

function buildChartData(series) {
  return YEARS.map((y, i) => ({ year: y, level: series[i] ?? null }));
}

function computeStats(series) {
  const base2010 = series[10];
  const latest   = series[24];
  const y1ago    = series[23];
  const cagr = latest && base2010 ? (Math.pow(latest / base2010, 1 / 14) - 1) * 100 : 0;
  const v1   = latest && y1ago    ? (latest / y1ago - 1) * 100 : 0;
  return { cagr, v1, latest };
}

export default function GenomaTesesSidebar() {
  const [idx,     setIdx]     = useState(0);
  const [showNcm, setShowNcm] = useState(true);

  const thesis = GENOMA_THESES[idx];
  const regime = REGIME_META[thesis.regime] || REGIME_META.robusto;
  const stats  = useMemo(() => computeStats(thesis.series), [thesis]);
  const data   = useMemo(() => buildChartData(thesis.series), [thesis]);
  const gradId = `sbgrad_${thesis.id}`;
  const isReal = thesis.dataTag.startsWith("REAL");

  const prev = () => setIdx((i) => (i - 1 + GENOMA_THESES.length) % GENOMA_THESES.length);
  const next = () => setIdx((i) => (i + 1) % GENOMA_THESES.length);

  return (
    <>
      <style>{FONT_IMPORT}{`* { box-sizing: border-box; }`}</style>

      <div style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        overflow: "hidden",
        fontFamily: "'IBM Plex Mono', monospace",
        color: C.white,
        position: "relative",
        width: "100%",
      }}>
        {/* Scanline */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
          backgroundImage: `linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)`,
          backgroundSize: "32px 32px" }} />

        <div style={{ position: "relative", zIndex: 1 }}>

          {/* ── HEADER ── */}
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: C.white }}>GENOMA TESES</span>
              <span style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: "0.1em", color: C.gold,
                background: "rgba(201,162,39,0.1)", border: "1px solid rgba(201,162,39,0.2)",
                padding: "2px 7px", borderRadius: 3 }}>ÍNDICE FÍSICO PROPRIETÁRIO</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={prev} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`,
                borderRadius: 4, color: C.dim, fontSize: 10, width: 24, height: 24,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "inherit", padding: 0 }}>◄</button>
              <span style={{ fontSize: 9, color: C.dimmer, minWidth: 32, textAlign: "center" }}>
                {idx + 1}/{GENOMA_THESES.length}
              </span>
              <button onClick={next} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`,
                borderRadius: 4, color: C.dim, fontSize: 10, width: 24, height: 24,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "inherit", padding: 0 }}>►</button>
            </div>
          </div>

          {/* ── THESIS IDENTITY ── */}
          <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5, flexWrap: "wrap" }}>
              <h3 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 700,
                color: C.white, margin: 0, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{thesis.title}</h3>
              <span style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: "0.08em", color: regime.color,
                padding: "2px 7px", borderRadius: 3,
                background: `${regime.color}15`, border: `1px solid ${regime.color}30` }}>
                {regime.dot} {regime.label}
              </span>
              <span style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: "0.08em",
                padding: "2px 7px", borderRadius: 3,
                background: isReal ? "rgba(52,211,153,0.1)" : "rgba(255,255,255,0.04)",
                color: isReal ? C.green : C.dimmer,
                border: `1px solid ${isReal ? C.green + "40" : C.border}` }}>
                {thesis.dataTag}
              </span>
            </div>
            <p style={{ fontSize: 10.5, color: C.dimmer, margin: 0, lineHeight: 1.4 }}>{thesis.subtitle}</p>
          </div>

          {/* ── STATS STRIP ── */}
          <div style={{ padding: "8px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 4 }}>
            {[
              { label: "CAGR 10A", val: stats.cagr, suffix: "% a.a." },
              { label: "VAR 1A",   val: stats.v1,   suffix: "%" },
              { label: "ÍNDICE",   val: stats.latest, raw: true },
            ].map(({ label, val, suffix, raw }) => {
              const isPos = val >= 0;
              const color = raw ? C.white : (isPos ? C.green : C.red);
              return (
                <div key={label} style={{ flex: 1, padding: "6px 8px",
                  background: "rgba(255,255,255,0.025)", borderRadius: 4, textAlign: "center" }}>
                  <div style={{ fontSize: 7, color: C.dimmer, letterSpacing: "0.1em", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color }}>
                    {raw ? val?.toFixed(1) : `${isPos ? "+" : ""}${val?.toFixed(1)}${suffix}`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── SPARKLINE ── */}
          <div style={{ padding: "8px 6px 0" }}>
            <ResponsiveContainer width="100%" height={130}>
              <AreaChart data={data} margin={{ top: 6, right: 16, left: 2, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={thesis.color} stopOpacity={0.35} />
                    <stop offset="80%"  stopColor={thesis.color} stopOpacity={0.04} />
                    <stop offset="100%" stopColor={thesis.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="year" stroke="#333" fontSize={7.5} tickLine={false} axisLine={false}
                  interval={4} tick={{ fill: "rgba(232,232,240,0.2)", fontFamily: "'IBM Plex Mono', monospace" }} />
                <YAxis hide domain={["dataMin - 8", "dataMax + 12"]} />
                <ReferenceLine y={100} stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3"
                  label={{ value: "base 100", fill: "rgba(232,232,240,0.15)", fontSize: 7,
                    position: "insideTopRight", fontFamily: "'IBM Plex Mono', monospace" }} />
                <Area type="monotone" dataKey="level" stroke={thesis.color} strokeWidth={2}
                  fill={`url(#${gradId})`} dot={false}
                  activeDot={{ r: 4, fill: thesis.color, stroke: C.card, strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* ── SIGNAL + INSIGHT ── */}
          <div style={{ padding: "8px 16px 10px", borderTop: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 7.5, color: C.dimmer, letterSpacing: "0.1em" }}>SINAL:</span>
            <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.06em",
              color: thesis.color, background: `${thesis.color}10`,
              border: `1px solid ${thesis.color}30`, padding: "2px 8px", borderRadius: 3 }}>
              {thesis.signal.toUpperCase()}
            </span>
            <span style={{ fontSize: 9, color: C.dimmer }}>{thesis.insight}</span>
          </div>

          {/* ── NCM CONSTITUENTS ── */}
          <div style={{ padding: "0 16px", borderTop: `1px solid ${C.border}` }}>
            <div style={{ paddingTop: 12, paddingBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 8, fontWeight: 700, color: C.dimmer, letterSpacing: "0.12em" }}>COMPOSIÇÃO NCM</span>
                <button onClick={() => setShowNcm(!showNcm)}
                  style={{ background: "transparent", border: "none", color: C.dimmer,
                    fontSize: 9, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                  {showNcm ? "▼" : "►"}
                </button>
                <span style={{ fontSize: 7.5, color: "rgba(232,232,240,0.15)" }}>
                  {thesis.constituents.length} NCMs · Siscomex/MDIC
                </span>
              </div>
              {showNcm && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 72px",
                    gap: 8, padding: "5px 8px", background: "rgba(255,255,255,0.02)",
                    borderRadius: 4, marginBottom: 2 }}>
                    {["NCM", "DESCRIÇÃO", "UNIDADE"].map(h =>
                      <span key={h} style={{ fontSize: 7.5, fontWeight: 700,
                        color: "rgba(232,232,240,0.2)", letterSpacing: "0.1em" }}>{h}</span>
                    )}
                  </div>
                  {thesis.constituents.map((c, i) => (
                    <div key={c.ncm} style={{ display: "grid", gridTemplateColumns: "90px 1fr 72px",
                      gap: 8, padding: "6px 8px", alignItems: "center",
                      borderBottom: i < thesis.constituents.length - 1 ? "1px solid #191924" : "none" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: thesis.color, letterSpacing: "0.04em" }}>{c.ncm}</span>
                      <span style={{ fontSize: 10, color: "rgba(232,232,240,0.5)", lineHeight: 1.3 }}>{c.desc}</span>
                      <span style={{ fontSize: 8.5, color: C.dimmer }}>{c.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── SCENARIO & METHODOLOGY ── */}
          <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${C.border}` }}>
            <div style={{ paddingTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ fontSize: 7.5, fontWeight: 700, color: C.dimmer,
                  letterSpacing: "0.12em", marginBottom: 7 }}>PREMONIÇÃO COGNITIVA</div>
                <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic",
                  fontSize: 10.5, color: "rgba(232,232,240,0.6)", lineHeight: 1.5, margin: 0,
                  borderLeft: `2px solid ${thesis.color}40`, paddingLeft: 9 }}>
                  {thesis.scenario}
                </p>
              </div>
              <div>
                <div style={{ fontSize: 7.5, fontWeight: 700, color: C.dimmer,
                  letterSpacing: "0.12em", marginBottom: 7 }}>METODOLOGIA</div>
                <p style={{ fontSize: 10, color: "rgba(232,232,240,0.4)", lineHeight: 1.5, margin: 0 }}>
                  {thesis.methodology}
                </p>
              </div>
            </div>
          </div>

          {/* ── CVM FOOTER ── */}
          <div style={{ margin: "0 16px 16px", padding: "7px 11px",
            background: "rgba(251,191,36,0.04)", border: "1px solid rgba(251,191,36,0.08)", borderRadius: 4 }}>
            <p style={{ fontSize: 8, color: "rgba(251,191,36,0.5)", lineHeight: 1.4, margin: 0 }}>
              <strong style={{ color: "rgba(251,191,36,0.7)" }}>⚠ AVISO CVM:</strong> Índice editorial proprietário do Relatório Reservado baseado em fluxos comerciais (Comexstat/MDIC). Não constitui recomendação de investimento.
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
